import type { ApolloClient } from 'apollo-client'
import { gql } from 'apollo-boost'
import type { QuoteFile } from 'paintscout'
import { createFile } from '../builder'
import { cloudinaryUploadFile } from '../cloudinary'
import type { UploadOptions } from './s3UploadFile'
import s3UploadFile from './s3UploadFile'
import {
  getFileKey,
  // generateVideoThumbnail,
  getRequiredTask,
  getUploadUrl
  // getMp4ThumbnailKey,
  // getPageCount
} from './util'
export interface UploadFileParams {
  file: File
  apolloClient: ApolloClient<any>
  uploadOptions: UploadOptions
}

export const RESIZE_IMAGE_FORMATS = ['jpeg', 'jpg', 'png', 'gif', 'svg']

export interface CsvOwner {
  value: string
  label: string
  email: string
  companyId?: string
}

/**
 * Which step of the upload chain a failure happened in. Stamped onto the thrown
 * error (`error.uploadStep`) so the dialog's failure handler can report it —
 * the iPad upload failures in #21104 are invisible without knowing whether it's
 * the S3 PUT (network/CORS), the GraphQL calls, or cloudinary that's failing.
 * `unsupportedFileType` is distinct from `processFile` so a blocked-by-upload-mode
 * failure (e.g. HEIC on the Cloudinary-only path) isn't confused with an actual
 * conversion/processing failure on the S3 path.
 */
export type UploadStep = 'cloudinary' | 'getUploadUrl' | 's3' | 'processFile' | 'unsupportedFileType'

export const HEIC_FILE_TYPES = ['heic', 'heif']

/**
 * Build the `processFile` gql query + variables for a given task/file, shared by
 * the generic fire-and-forget call and the post-heic-conversion imageResize kick-off.
 */
function buildProcessFileRequest({
  task,
  srcUrl,
  srcKey,
  contentType,
  srcBucket,
  initialImageWidth,
  uploadOptions
}: {
  task: string
  srcUrl: string
  srcKey: string
  contentType: string
  srcBucket: string
  initialImageWidth: number
  uploadOptions: UploadOptions
}) {
  return {
    query: gql`
      query processFile($task: String!, $fileParams: ProcessFileParams!, $uploadOptions: UploadOptions!) {
        processFile(task: $task, fileParams: $fileParams, uploadOptions: $uploadOptions)
      }
    `,
    variables: {
      task,
      fileParams: {
        srcUrl,
        srcKey,
        contentType,
        srcBucket,
        initialImageWidth
      },
      uploadOptions
    },
    fetchPolicy: 'network-only' as const
  }
}

/**
 * Run an upload step, tagging any thrown error with the step name (without
 * clobbering a more specific tag a nested call already set) and re-throwing it
 * unchanged otherwise.
 */
async function withStep<T>(step: UploadStep, run: () => Promise<T>): Promise<T> {
  try {
    return await run()
  } catch (error) {
    if (error && typeof error === 'object' && !(error as { uploadStep?: UploadStep }).uploadStep) {
      ;(error as { uploadStep?: UploadStep }).uploadStep = step
    }
    throw error
  }
}

export default async function uploadFile({ file, apolloClient, uploadOptions }: UploadFileParams): Promise<QuoteFile> {
  const { fileKey, initialImageWidth, fileType } = getFileKey(file, uploadOptions)
  const { s3UploadFeatureEnabled, s3MediaUploadEnabled } = uploadOptions

  // HEIC is only convertible via the S3 upload path (server-side heicConvert task).
  // Companies still on Cloudinary-only uploads get a clear, immediate failure instead
  // of silently uploading an unrenderable HEIC file to Cloudinary.
  if (HEIC_FILE_TYPES.includes(fileType) && !s3UploadFeatureEnabled) {
    const error: Error & { uploadStep?: UploadStep } = new Error('HEIC images are not supported for this upload mode.')
    error.uploadStep = 'unsupportedFileType'
    throw error
  }

  // Videos/PDFs were historically forced to Cloudinary for server-side
  // derivatives (PDF per-page pngs, video transcodes). With the `s3MediaUpload`
  // flag on, the new FileViewer renders them straight from S3 (react-pdf pages,
  // native <video preload="metadata"> first-frame thumbs), so we skip the
  // file-type forcing and let them land on S3 like images. The
  // `!s3UploadFeatureEnabled` fallback below is intentionally left untouched.
  const forceCloudinaryForMedia =
    !s3MediaUploadEnabled &&
    (file?.name?.toLowerCase().endsWith('.mov') ||
      ['video/quicktime'].includes(file.type) ||
      ['mp4', 'pdf'].includes(fileType))

  if (forceCloudinaryForMedia || !s3UploadFeatureEnabled) {
    const { secure_url, format, pages, public_id, width, height, type } = await withStep('cloudinary', () =>
      cloudinaryUploadFile(file, uploadOptions)
    )

    const quoteFile = createFile({ type: file.type })

    return {
      ...quoteFile,
      src: secure_url,
      cloudinaryPublicId: public_id,
      s3PublicKey: null,
      format,
      pages,
      width,
      height,
      type
    }
  }

  // Get a signed url to upload the file to
  const signedUploadUrl = await withStep('getUploadUrl', () => getUploadUrl({ apolloClient, fileKey }))

  // Upload the file to s3
  const { bucket, ...uploadFileData } = await withStep('s3', () => s3UploadFile(file, signedUploadUrl))

  // Todo: Handle PDF's as they come in to get pages
  // Currently just using cloudinary for pdfs
  // if (file.type.includes('pdf')) {
  //   const pages = await getPageCount(file)
  //   let base64pdf: any
  //   let binaryPdf: any
  //   const fileReader = new FileReader()
  //   // Onload of file read the file content
  //   fileReader.onload = (fileLoadedEvent) => {
  //     base64pdf = fileLoadedEvent.target.result
  //     console.log('base64pdf', base64pdf)
  //     console.log('base64pdf noStart', base64pdf.split(',')[1])
  //     console.log('base64pdf noStart', atob(base64pdf.split(',')[1]))
  //     binaryPdf = atob(base64pdf.split(',')[1])
  //     // const loadData = await pdfImgConvert.convert(binaryPdf, { height: 792, width: 612, page_numbers: [i] })
  //   }
  //   // Convert data to base64
  //   fileReader.readAsDataURL(file)
  //   for (let i = 1; i <= pages; i++) {
  //     console.log('pages', pages)
  //     console.log('i', i)
  //     // const loadData = await pdfjsLib.convert(binaryPdf, { height: 792, width: 612, page_numbers: [i] })
  //   }
  // }

  // Create thumbnail from mp4 and save
  // Mp4 through cloudinary atm
  // if (file.type.includes('mp4')) {
  //   const thumbFile = await generateVideoThumbnail(file)
  //   const thumbKey = getMp4ThumbnailKey(fileKey)
  //   const signedUploadThumbDetails = await getSignedUpload(apolloClient, thumbKey, 'jpeg')
  //   await s3UploadFile(thumbFile, {
  //     companyId,
  //     docId,
  //     signedUploadDetails: signedUploadThumbDetails
  //   })
  // }

  // Trigger the processing for files to get all sizes/views we need
  const requiredTask = getRequiredTask(fileType)

  let finalUploadData = uploadFileData
  let finalType = file.type

  if (requiredTask === 'heicConvert') {
    // HEIC can't be rendered by any browser, so conversion has to happen
    // synchronously here — there's no "processing" state on QuoteFile to update
    // once some later fire-and-forget job finishes.
    const { data } = await withStep('processFile', () =>
      apolloClient.query({
        query: gql`
          query convertHeicToJpeg($fileParams: ProcessFileParams!, $uploadOptions: UploadOptions!) {
            convertHeicToJpeg(fileParams: $fileParams, uploadOptions: $uploadOptions) {
              src
              s3PublicKey
              bucket
              format
              type
              width
              height
              size
            }
          }
        `,
        variables: {
          fileParams: {
            srcUrl: uploadFileData.src,
            srcKey: uploadFileData.s3PublicKey,
            contentType: fileType,
            srcBucket: bucket,
            initialImageWidth
          },
          // s3MediaUploadEnabled is a client-only routing flag, not part of the server's UploadOptions schema
          uploadOptions: (({ s3MediaUploadEnabled: _s3MediaUploadEnabled, ...rest }) => rest)(uploadOptions)
        },
        fetchPolicy: 'network-only'
      })
    )

    // Keep `bucket` out of finalUploadData — same as uploadFileData above, it's only
    // needed to address the follow-up processFile call, not part of the persisted QuoteFile.
    const { bucket: convertedBucket, ...convertedFileData } = data.convertHeicToJpeg
    finalUploadData = { ...uploadFileData, ...convertedFileData }
    // The converted file's mime type must win over the original HEIC's — createFile()
    // is spread after finalUploadData below, so `type` has to be set correctly here.
    finalType = 'image/jpeg'

    // Now that we have a real jpg, kick off derivative-size generation exactly like a
    // native jpg upload would (fire-and-forget, same as the `else if` branch below). This
    // is best-effort: the conversion above already succeeded and the jpg is already usable
    // on S3, so a resize hiccup here shouldn't fail the whole upload out from under the
    // user — unlike the `else if` branch, don't let this throw.
    try {
      await apolloClient.query(
        buildProcessFileRequest({
          task: 'imageResize',
          srcUrl: finalUploadData.src,
          srcKey: finalUploadData.s3PublicKey,
          contentType: 'jpg',
          srcBucket: convertedBucket,
          initialImageWidth,
          uploadOptions
        })
      )
    } catch (error) {
      console.error('Failed to kick off imageResize after heic conversion (non-fatal)', error)
    }
  } else if (requiredTask) {
    await withStep('processFile', () =>
      apolloClient.query(
        buildProcessFileRequest({
          task: requiredTask,
          srcUrl: uploadFileData.src,
          srcKey: uploadFileData.s3PublicKey,
          contentType: fileType,
          srcBucket: bucket,
          initialImageWidth,
          uploadOptions
        })
      )
    )
  }

  // Turn into QuoteFile
  const quoteFile = createFile({ type: finalType })

  const res: QuoteFile = {
    ...finalUploadData,
    ...quoteFile,
    cloudinaryPublicId: null
  }

  return res
}
